import { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import type { CaseResponseDto } from '@bandido/shared/dtos';
import { OriginatedByChannel } from '@bandido/shared/types';
import { useTranslation } from 'react-i18next';

import { CONFIG } from '@frontend/config';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  ClientSelectorModal,
  type ClientSelectorTableItem,
} from '@frontend/features/clients/components/ClientSelectorModal';
import {
  bookingCaseCreationReady,
  operatingAgencyId,
} from '../bookingCaseContext';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (caseData: CaseResponseDto) => void;
}

export function CreateBookingCaseDialog({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const [subject, setSubject] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientSelectorTableItem | null>(null);
  const [clientSelectorOpen, setClientSelectorOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const agencyId = operatingAgencyId(CONFIG.AGENCY_ID);

  const close = () => {
    if (creating) return;
    setSubject('');
    setSelectedClient(null);
    setClientSelectorOpen(false);
    onClose();
  };

  const create = async () => {
    if (!selectedClient || !subject.trim()) return;
    setCreating(true);
    try {
      const created = await client.post('/case', {
        name: subject.trim(),
        agencyId,
        clientId: selectedClient?.id,
        originatedByChannel: OriginatedByChannel.MANUAL,
      });
      addToast({ message: t('cases.create.createdSuccess'), color: 'success' });
      setSubject('');
      setSelectedClient(null);
      // Alcanza para seleccionar inmediatamente; el contexto de la página
      // consulta el DTO completo con el id recién creado sin cerrar la oferta.
      onCreated({
        id: created.data.id,
        name: subject.trim(),
        agencyId,
        clientId: selectedClient.id,
        clientFirstName: selectedClient.name,
        clientLastName: '',
      } as CaseResponseDto);
      onClose();
    } catch {
      addToast({ message: t('cases.create.createdError'), color: 'error' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{t('travelServices.booking.createCaseTitle')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              autoFocus
              fullWidth
              value={subject}
              disabled={creating}
              label={t('travelServices.booking.createCaseSubject')}
              onChange={(event) => setSubject(event.target.value)}
            />
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {t('travelServices.booking.createCaseClient')}
              </Typography>
              <Button variant="outlined" disabled={creating} onClick={() => setClientSelectorOpen(true)}>
                {selectedClient?.name ?? t('travelServices.booking.selectClient')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={creating}>
            {t('common.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            loading={creating}
            disabled={!bookingCaseCreationReady(subject, selectedClient?.id)}
            onClick={() => void create()}
          >
            {t('travelServices.booking.createCase')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <ClientSelectorModal
        open={clientSelectorOpen}
        agencyId={agencyId}
        selectedItemId={selectedClient?.id}
        onClose={() => setClientSelectorOpen(false)}
        onSelect={(clientItem) => {
          setSelectedClient(clientItem);
          setClientSelectorOpen(false);
        }}
      />
    </>
  );
}
